import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import api, { formatErr } from "@/lib/api";
import { applyTheme, DEFAULT_THEME } from "@/lib/themes";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branchId, setBranchId] = useState(
    localStorage.getItem("erp_branch_id") || "",
  );
  const [sessionId, setSessionId] = useState(
    localStorage.getItem("erp_session_id") || "",
  );
  const [themeKey, setThemeKey] = useState(
    localStorage.getItem("erp_theme") || DEFAULT_THEME,
  );
  const [theme, setTheme] = useState(() => applyTheme(themeKey));
  const [lang, setLang] = useState(localStorage.getItem("erp_lang") || "en");

  // Apply theme on mount + any change
  useEffect(() => {
    const t = applyTheme(themeKey);
    setTheme(t);
    localStorage.setItem("erp_theme", themeKey);
  }, [themeKey]);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem("erp_token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (!branchId && data.branch_id) {
        setBranchId(data.branch_id);
        localStorage.setItem("erp_branch_id", data.branch_id);
      }
      if (!sessionId && data.session_id) {
        setSessionId(data.session_id);
        localStorage.setItem("erp_session_id", data.session_id);
      }
    } catch {
      localStorage.removeItem("erp_token");
      localStorage.removeItem("erp_user");
      setUser(null);
    }
    setLoading(false);
  }, []); // eslint-disable-line

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (identifier, password) => {
    const endpoints = [
      "/auth/signin",
      "/auth/login",
      "/auth/token",
      "/session/login",
      "/token",
    ];

    try {
      let data = null;
      let lastError = null;

      for (const endpoint of endpoints) {
        try {
          const response = await api.post(endpoint, { identifier, password });
          data = response?.data || null;
          if (data) break;
        } catch (err) {
          lastError = err;
        }
      }

      if (!data) {
        throw lastError || new Error("Login endpoint unavailable");
      }

      localStorage.setItem("erp_token", data.token);
      localStorage.setItem("erp_user", JSON.stringify(data.user));
      if (data.branch_id) {
        localStorage.setItem("erp_branch_id", data.branch_id);
        setBranchId(data.branch_id);
      }
      if (data.session_id) {
        localStorage.setItem("erp_session_id", data.session_id);
        setSessionId(data.session_id);
      }
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return {
        ok: false,
        error: formatErr(e.response?.data?.detail) || e.message,
      };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem("erp_token");
    localStorage.removeItem("erp_user");
    setUser(null);
  };

  const updateBranch = (id) => {
    localStorage.setItem("erp_branch_id", id);
    setBranchId(id);
  };
  const updateSession = (id) => {
    localStorage.setItem("erp_session_id", id);
    setSessionId(id);
  };
  const updateTheme = (k) => setThemeKey(k);
  const updateLang = (l) => {
    localStorage.setItem("erp_lang", l);
    setLang(l);
  };

  const isSuperAdmin = user?.role === "superadmin";

  return (
    <AuthCtx.Provider
      value={{
        user,
        loading,
        login,
        logout,
        branchId,
        sessionId,
        updateBranch,
        updateSession,
        themeKey,
        theme,
        updateTheme,
        lang,
        updateLang,
        isSuperAdmin,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
